import { Loader } from '@googlemaps/js-api-loader';
import { useTranslation } from 'next-i18next';
import { useState, useEffect, ChangeEvent, createRef, RefObject, Dispatch, SetStateAction } from 'react';
import { IUserLocation, IGoogleMapLocation } from '@features/checkout/typescript';
import Button from '@components/Button/Button';
import Icon from '@components/icons/Icon';
import { getUserGeolocation, getUserAddress } from '@utils/showroom';
import styles from './SearchShowroom.styles';
let autocomplete: google.maps.places.Autocomplete | null;
let autocompleteEvent: google.maps.MapsEventListener | null;
let searchTimeOut: NodeJS.Timeout | null;
type PropsType = {
  loader: Loader;
  userLocation: IUserLocation | undefined;
  setUserLocation: Dispatch<SetStateAction<IUserLocation | undefined>>;
  searchShowrooms: (searchWord: string | null, location: IGoogleMapLocation | null, htmlDivElement: HTMLDivElement) => void;
  getClearUserLocation: () => IUserLocation;
};
const SearchShowroom = ({
  loader,
  userLocation,
  setUserLocation,
  searchShowrooms,
  getClearUserLocation
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const htmlDivElement: RefObject<HTMLDivElement | null> = createRef();
  const searchInput: RefObject<HTMLInputElement | null> = createRef();
  const [iconCloseIsVisible, setIconCloseIsVisible] = useState<boolean>(false);
  const [info, setInfo] = useState<string>('');
  const autocompleteOptions = {
    types: ['(regions)'],
    fields: ['address_components', 'geometry']
  };
  const clearTimeOut = () => {
    if (searchTimeOut) {
      clearTimeout(searchTimeOut);
    }
  };
  const handleIconCloseIsVisible = () => {
    if (searchInput?.current) {
      setIconCloseIsVisible(searchInput?.current.value.length > 0);
    }
  };
  const handleClearInput = () => {
    if (searchInput?.current && htmlDivElement?.current) {
      searchInput.current.value = '';
      searchShowrooms('', null, htmlDivElement.current);
      handleIconCloseIsVisible();
    }
  };
  const handleChange = (newSearchWord: string) => {
    if (htmlDivElement?.current) {
      clearTimeOut();
      handleIconCloseIsVisible();
      searchTimeOut = setTimeout(() => {
        if (htmlDivElement?.current) {
          searchShowrooms(newSearchWord, null, htmlDivElement.current);
        }
      }, 3000);
    }
  };
  const handleSearchInputAutocomplete = () => {
    if (searchInput?.current) {
      if (!autocomplete) autocomplete = new google.maps.places.Autocomplete(searchInput.current, autocompleteOptions);
      if (autocompleteEvent) google.maps.event.removeListener(autocompleteEvent);
      autocompleteEvent = autocomplete.addListener('place_changed', () => {
        if (autocomplete) {
          const place = autocomplete.getPlace();
          if (place?.geometry?.location && htmlDivElement?.current) {
            clearTimeOut();
            searchShowrooms(null, {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            }, htmlDivElement.current);
          }
        }
      });
    }
  };
  const setSearchPositionByUserPosition = (newUserLocation: IUserLocation) => {
    if (newUserLocation.location.lat && newUserLocation.location.lng && htmlDivElement?.current) {
      searchShowrooms(null, {
        lat: newUserLocation.location.lat,
        lng: newUserLocation.location.lng
      }, htmlDivElement.current);
    }
  };
  const handleGeolocationUnsuccessful = () => {
    setInfo(t('geolocationUnsuccessful'));
  };
  const setUserCityAndCountry = (newUserLocation: IUserLocation) => {
    if (newUserLocation.city && newUserLocation.country && searchInput?.current) {
      searchInput.current.value = `${newUserLocation.city}, ${newUserLocation.country}`;
    }
  };
  const handleLocalization = async () => {
    if (userLocation && userLocation.city && userLocation.location.lat && userLocation.location.lng) {
      setUserCityAndCountry(userLocation);
      setSearchPositionByUserPosition(userLocation);
      handleIconCloseIsVisible();
    } else if (userLocation) {
      const newUserGeolocation = await getUserGeolocation();
      const newUserAddress = await getUserAddress({
        ...userLocation,
        location: newUserGeolocation
      });
      if (newUserAddress.city && newUserAddress.country) {
        setUserCityAndCountry(newUserAddress);
        setSearchPositionByUserPosition(newUserAddress);
        setUserLocation(getClearUserLocation());
        setUserLocation(newUserAddress);
        handleIconCloseIsVisible();
      } else {
        handleGeolocationUnsuccessful();
      }
    }
  };
  useEffect(() => {
    void loader.load().then(() => {
      handleSearchInputAutocomplete();
    });
    return () => {
      if (autocompleteEvent) google.maps.event.removeListener(autocompleteEvent);
      autocompleteEvent = null;
      autocomplete = null;
      clearTimeOut();
    };
  });
  return <div sx={styles.container}>
      <div sx={styles.emptyDiv} ref={htmlDivElement} />
      <div sx={styles.searchInputContainer}>
        <div sx={styles.searchIconContainer}>
          <Icon icon="Search" size="20px" />
        </div>
        <input type="text" placeholder={t('searchShowroom')} onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e.target.value)} ref={searchInput} autoComplete="off" />
        <div sx={styles.navigationButtonContainer}>
          {iconCloseIsVisible ? <Button variant="iconOnly" onClick={handleClearInput} aria-label={t('clearSearch')}>
              <Icon icon="Close" size="24px" />
            </Button> : <Button variant="iconOnly" onClick={handleLocalization} aria-label={t('searchShowroom')}>
              <Icon icon="LocationSearching" size="24px" />
            </Button>}
        </div>
      </div>
      {info && <div sx={styles.infoContainer}>{info}</div>}
    </div>;
};
export default SearchShowroom;